import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { Locations } from '@/domain/bioscopes';
import { formatDateTimeWorded, toISOString } from '@/utils/date';

import styles from './BioscopePosition.module.scss';

interface BioscopePositionProps {
    title?: string;
    bioscopeLocations: Locations[];
    /** Shows which client the unit belonged to during each placement. Internal pages only. */
    showClient?: boolean;
}

function PositionLink(props: { bioscopeLocation: Locations }) {
    const { bioscopeLocation } = props;
    const { t } = useTranslation();
    const cage = bioscopeLocation.cages;
    const location = cage?.locations;
    if (!cage || !location) return null;

    return (
        <Link
            to={`/c/${location.clientId}/location/${location.id}/pen/${cage.id}?from=${toISOString(bioscopeLocation.from)}${
                bioscopeLocation.to != null ? `&to=${toISOString(bioscopeLocation.to)}` : ''
            }`}>
            {t('common.actions.goTo')}
        </Link>
    );
}

function BioscopePosition({
    bioscopeLocations,
    title = 'molecules.bioscopePosition.position',
    showClient = false,
}: BioscopePositionProps) {
    const { t } = useTranslation();

    const clientColumn: DataTableColumnDef<Locations> = {
        id: 'client',
        header: t('common.labels.client'),
        cell: ({ row }) => row.original.clientName ?? t('common.labels.nonExistentClient'),
    };

    const columns: DataTableColumnDef<Locations>[] = [
        {
            id: 'from',
            header: t('common.labels.from'),
            cell: ({ row }) => formatDateTimeWorded(row.original.from, 'minute'),
        },
        {
            id: 'to',
            header: t('molecules.common.to'),
            cell: ({ row }) =>
                row.original.to
                    ? formatDateTimeWorded(row.original.to, 'minute')
                    : t('bioscopeDetail.position.now'),
        },
        ...(showClient ? [clientColumn] : []),
        {
            id: 'cage',
            header: t('common.labels.cage'),
            cell: ({ row }) =>
                !row.original.cages ? t('common.labels.land') : row.original.cages.name,
        },
        {
            id: 'location',
            header: t('common.labels.location'),
            cell: ({ row }) =>
                !row.original.cages ? t('common.labels.land') : row.original.cages.locations?.name,
        },
        {
            id: 'contract',
            header: t('bioscopeDetail.position.contract'),
            cell: ({ row }) => row.original?.contractDescription,
        },
        {
            id: 'link',
            header: '',
            meta: { headerScreenReaderText: t('common.labels.cage') },
            cell: ({ row }) => <PositionLink bioscopeLocation={row.original} />,
        },
    ];

    return (
        <div className={styles.bioscopePosition}>
            <h2>{t(title)}</h2>
            <DataTable
                data={bioscopeLocations}
                columns={columns}
                getRowId={(bioscopeLocation) => String(bioscopeLocation.id)}
                responsive
                density="compact"
                enableSorting={false}
                hover={false}
                emptyState={t('bioscopeDetail.position.noLocations')}
            />
        </div>
    );
}

export default BioscopePosition;
export { BioscopePosition };
